import React, { useState, useEffect } from 'react';
import { AppIcon, KNOWN_APPLE_ARTWORKS, getBrandStyleForApp } from '../components/AppIcon';
import { purgeOldIconCaches, ICON_CACHE_VERSION_KEY } from '../services/iconCache';

interface DebugAppInfo {
  name: string;
  appStoreId: string;
  apiStatus: 'PENDING' | 'SUCCESS' | 'ERROR';
  iconUrl: string;
  imgStatus: 'PENDING' | 'LOADED' | 'FAILED';
  naturalWidth: number;
  naturalHeight: number;
  errorDetails?: string;
}

const TEST_APPS = [
  { name: 'Duolingo', appStoreId: '570060128', expectedSlug: 'duolingo' },
  { name: 'ChatGPT', appStoreId: '6448311069', expectedSlug: 'chatgpt' },
  { name: 'Spotify', appStoreId: '324684580', expectedSlug: 'spotify' },
  { name: 'TikTok', appStoreId: '835599320', expectedSlug: 'tiktok' },
  { name: 'Canva', appStoreId: '897446215', expectedSlug: 'canva' },
  { name: 'CapCut', appStoreId: '1500855883', expectedSlug: 'capcut' },
  { name: 'Notion', appStoreId: '1232780281', expectedSlug: 'notion' }
];

export const DebugIconsView: React.FC = () => {
  const [debugData, setDebugData] = useState<Record<string, DebugAppInfo>>(() => {
    const initial: Record<string, DebugAppInfo> = {};
    TEST_APPS.forEach((app) => {
      initial[app.appStoreId] = {
        name: app.name,
        appStoreId: app.appStoreId,
        apiStatus: 'PENDING',
        iconUrl: KNOWN_APPLE_ARTWORKS[app.appStoreId] || '',
        imgStatus: 'PENDING',
        naturalWidth: 0,
        naturalHeight: 0
      };
    });
    return initial;
  });

  useEffect(() => {
    // Purge legacy caches and initialize V2
    purgeOldIconCaches();

    // Verify Apple Public Lookup API endpoint
    TEST_APPS.forEach(async (app) => {
      try {
        let artworkUrl = KNOWN_APPLE_ARTWORKS[app.appStoreId] || '';
        const res = await fetch(`/api/apple/lookup?id=${app.appStoreId}`);
        if (res.ok) {
          const json = await res.json();
          if (json.results && json.results[0]) {
            const itm = json.results[0];
            artworkUrl = itm.artworkUrl512 || itm.artworkUrl100 || itm.artworkUrl60 || artworkUrl;
            console.log(`APPLE RESULT for ${app.name}:`, {
              name: itm.trackName,
              appStoreId: app.appStoreId,
              iconUrl: artworkUrl
            });
          }
        }

        setDebugData((prev) => ({
          ...prev,
          [app.appStoreId]: {
            ...prev[app.appStoreId],
            apiStatus: artworkUrl ? 'SUCCESS' : 'ERROR',
            iconUrl: artworkUrl
          }
        }));
      } catch (err: any) {
        console.error(`Lookup failed for ${app.name}:`, err);
        setDebugData((prev) => ({
          ...prev,
          [app.appStoreId]: {
            ...prev[app.appStoreId],
            apiStatus: 'ERROR',
            errorDetails: err.message
          }
        }));
      }
    });
  }, []);

  const handleImageLoad = (appStoreId: string, e: React.SyntheticEvent<HTMLImageElement>) => {
    const img = e.currentTarget;
    console.log(`APP ICON LOADED for ID ${appStoreId}:`, {
      src: img.src,
      naturalWidth: img.naturalWidth,
      naturalHeight: img.naturalHeight
    });

    setDebugData((prev) => ({
      ...prev,
      [appStoreId]: {
        ...prev[appStoreId],
        imgStatus: img.naturalWidth > 0 && img.naturalHeight > 0 ? 'LOADED' : 'FAILED',
        naturalWidth: img.naturalWidth,
        naturalHeight: img.naturalHeight
      }
    }));
  };

  const handleImageError = (appStoreId: string, e: React.SyntheticEvent<HTMLImageElement>) => {
    console.error(`APP ICON FAILED for ID ${appStoreId}:`, e.currentTarget.src);
    setDebugData((prev) => ({
      ...prev,
      [appStoreId]: {
        ...prev[appStoreId],
        imgStatus: 'FAILED'
      }
    }));
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-12">
      <div className="mb-8 pb-6 border-b border-slate-200">
        <div className="flex items-center gap-3 mb-2">
          <span className="px-2.5 py-1 rounded-md bg-emerald-100 text-emerald-800 text-xs font-bold uppercase tracking-wider">
            Diagnostics Mode
          </span>
          <span className="text-xs text-slate-500 font-mono">/debug-icons</span>
        </div>
        <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-900 tracking-tight">
          Official App Store Artwork & AppIcon Diagnostics
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          Direct verification of Apple 512x512 artworks, natural dimensions, fallback layers, and live image rendering.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {TEST_APPS.map((app) => {
          const info = debugData[app.appStoreId] || {
            name: app.name,
            appStoreId: app.appStoreId,
            apiStatus: 'PENDING',
            iconUrl: '',
            imgStatus: 'PENDING',
            naturalWidth: 0,
            naturalHeight: 0
          };
          const brandStyle = getBrandStyleForApp(app.name);

          return (
            <div
              key={app.appStoreId}
              className="bg-white rounded-2xl p-6 border border-slate-200/80 shadow-xs flex flex-col justify-between"
            >
              <div>
                <div className="flex items-start justify-between gap-4 mb-4">
                  <div>
                    <h3 className="text-lg font-bold text-slate-900">{app.name}</h3>
                    <p className="text-xs text-slate-500 font-mono">App Store ID: {app.appStoreId}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    <span
                      className={`px-2 py-0.5 rounded text-[11px] font-bold ${
                        info.apiStatus === 'SUCCESS'
                          ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                          : info.apiStatus === 'ERROR'
                          ? 'bg-red-50 text-red-700 border border-red-200'
                          : 'bg-slate-100 text-slate-600'
                      }`}
                    >
                      API: {info.apiStatus}
                    </span>
                    <span
                      className={`px-2 py-0.5 rounded text-[11px] font-bold ${
                        info.imgStatus === 'LOADED'
                          ? 'bg-blue-50 text-blue-700 border border-blue-200'
                          : info.imgStatus === 'FAILED'
                          ? 'bg-red-50 text-red-700 border border-red-200'
                          : 'bg-amber-50 text-amber-700 border border-amber-200'
                      }`}
                    >
                      IMG: {info.imgStatus}
                    </span>
                  </div>
                </div>

                {/* Metrics Breakdown */}
                <div className="bg-slate-50 rounded-xl p-3 border border-slate-100 mb-5 space-y-1.5 text-xs font-mono">
                  <div className="flex justify-between items-center text-slate-600">
                    <span>naturalWidth:</span>
                    <span className="font-bold text-slate-900">{info.naturalWidth}px</span>
                  </div>
                  <div className="flex justify-between items-center text-slate-600">
                    <span>naturalHeight:</span>
                    <span className="font-bold text-slate-900">{info.naturalHeight}px</span>
                  </div>
                  <div className="pt-1.5 border-t border-slate-200/60">
                    <span className="text-slate-500 block mb-0.5">Resolved Artwork URL:</span>
                    <div className="text-[10px] text-blue-600 truncate bg-white p-1.5 rounded border border-slate-200 select-all">
                      {info.iconUrl || 'Resolving...'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Side-by-side Visual Comparison */}
              <div className="pt-4 border-t border-slate-100 flex items-center justify-around gap-4 text-center">
                {/* 1. Full Production AppIcon */}
                <div className="flex flex-col items-center gap-1.5">
                  <div className="w-16 h-16 rounded-2xl shadow-sm border border-slate-200 flex items-center justify-center p-0.5 bg-slate-50">
                    <AppIcon
                      app={{
                        id: app.expectedSlug,
                        name: app.name,
                        appStoreId: Number(app.appStoreId),
                        iconUrl: info.iconUrl
                      }}
                      size={60}
                      rounded="xl"
                    />
                  </div>
                  <span className="text-[11px] font-bold text-slate-700">AppIcon Component</span>
                  <span className="text-[10px] text-emerald-600 font-semibold">Real Artwork Layer</span>
                </div>

                {/* 2. Direct Raw Image Element */}
                <div className="flex flex-col items-center gap-1.5">
                  <div className="w-16 h-16 rounded-2xl shadow-sm border border-slate-200 overflow-hidden relative bg-slate-100 flex items-center justify-center">
                    {info.iconUrl ? (
                      <img
                        src={info.iconUrl}
                        alt={`${app.name} Raw`}
                        className="w-full h-full object-cover"
                        crossOrigin="anonymous"
                        onLoad={(e) => handleImageLoad(app.appStoreId, e)}
                        onError={(e) => handleImageError(app.appStoreId, e)}
                      />
                    ) : (
                      <span className="text-xs text-slate-400">Loading</span>
                    )}
                  </div>
                  <span className="text-[11px] font-bold text-slate-700">Raw &lt;img&gt;</span>
                  <span className="text-[10px] text-slate-500">512x512 Native</span>
                </div>

                {/* 3. Fallback Monogram for comparison */}
                <div className="flex flex-col items-center gap-1.5">
                  <div
                    className={`w-16 h-16 rounded-2xl shadow-inner border border-white/20 flex items-center justify-center bg-gradient-to-br ${brandStyle.gradient} ${brandStyle.textColor}`}
                  >
                    <span className="text-2xl font-black">{brandStyle.initial || app.name.charAt(0)}</span>
                  </div>
                  <span className="text-[11px] font-bold text-slate-700">Fallback Layer</span>
                  <span className="text-[10px] text-slate-400">z-index: 1 Underneath</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default DebugIconsView;
